import { Button, VStack } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { now, toApiDate } from "@src/utils/dates";
import type { CalendarValues } from "@uselessdev/datepicker";
import {
  addDays,
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from "date-fns";
import { useMemo } from "react";

export type CalendarActionsProps = {
  setDates: (dates: CalendarValues) => void;
  customActions?: CalendarAction[];
};

export type CalendarAction = {
  title: string;
  getDates: () => CalendarValues;
};

export type DateRangePresetKey =
  | "today"
  | "next-7-days"
  | "next-14-days"
  | "next-30-days"
  | "this-month"
  | "last-month"
  | "this-year"
  | "last-year";

export type DateRangePreset = CalendarAction & { key: DateRangePresetKey };

export const getDateRangePresets = (): DateRangePreset[] => {
  const today = now();

  return [
    {
      key: "today",
      title: t`Today`,
      getDates: () => ({ start: today, end: today }),
    },
    {
      key: "next-7-days",
      title: t`Next 7 days`,
      getDates: () => ({ start: today, end: addDays(today, 7) }),
    },
    {
      key: "next-14-days",
      title: t`Next 14 days`,
      getDates: () => ({ start: today, end: addDays(today, 14) }),
    },
    {
      key: "next-30-days",
      title: t`Next 30 days`,
      getDates: () => ({ start: today, end: addDays(today, 30) }),
    },
    {
      key: "this-month",
      title: t`This month`,
      getDates: () => ({
        start: startOfMonth(today),
        end: endOfMonth(today),
      }),
    },
    {
      key: "last-month",
      title: t`Last month`,
      getDates: () => ({
        start: startOfMonth(subMonths(today, 1)),
        end: endOfMonth(subMonths(today, 1)),
      }),
    },
    {
      key: "this-year",
      title: t`This year`,
      getDates: () => ({ start: startOfYear(today), end: endOfYear(today) }),
    },
    {
      key: "last-year",
      title: t`Last year`,
      getDates: () => ({
        start: startOfYear(subYears(today, 1)),
        end: endOfYear(subYears(today, 1)),
      }),
    },
  ];
};

export const isDateRangePresetKey = (
  value: string,
): value is DateRangePresetKey =>
  getDateRangePresets().some((preset) => preset.key === value);

export const resolveDateRangePreset = (
  key: DateRangePresetKey,
): [string, string] | undefined => {
  const { start, end } =
    getDateRangePresets()
      .find((preset) => preset.key === key)
      ?.getDates() ?? {};
  if (!start || !end) return undefined;

  return [toApiDate(start), toApiDate(end)];
};

export const getDateRangePresetTitle = (
  key: DateRangePresetKey,
): string | undefined =>
  getDateRangePresets().find((preset) => preset.key === key)?.title;

export const CalendarActions = ({
  setDates,
  customActions,
}: CalendarActionsProps) => {
  const actionItems = useMemo(
    () => customActions ?? getDateRangePresets(),
    [customActions],
  );

  return (
    <VStack
      align="stretch"
      px={{
        base: "1",
        md: "3",
      }}
      py="4"
      borderColor="grey.100"
      borderRightWidth="1px"
      spacing="2"
    >
      {actionItems.map((item) => (
        <Button
          key={item.title}
          justifyContent="start"
          colorScheme="grey"
          onClick={() => setDates(item.getDates())}
          size="xs"
          variant="ghost"
        >
          {item.title}
        </Button>
      ))}
    </VStack>
  );
};
